import React, { useState, useEffect } from 'react';
import { Calendar, Filter, Download, Eye, AlertCircle, Clock, User, Activity, Building, Shield } from 'lucide-react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import AdminLayout from '../components/AdminLayout';
import api from '../services/api';

const AdminAuditTrails = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});

  // Filters
  const [filters, setFilters] = useState({
    page: 1,
    per_page: 10,
    organization_id: '',
    category: '',
    action: '',
    actor_type: '',
    severity: '',
    status: '',
    start_date: '',
    end_date: ''
  });

  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  // Available filter options
  const categories = ['agents', 'clients', 'users', 'messages', 'documents', 'auth', 'settings', 'admin', 'organizations', 'system'];
  const actions = ['create', 'update', 'delete', 'view', 'login', 'logout', 'upload', 'send', 'verify', 'suspend'];
  const actorTypes = ['user', 'admin', 'system'];
  const severities = ['low', 'medium', 'high', 'critical'];
  const statuses = ['success', 'failed', 'unauthorized', 'warning'];

  useEffect(() => {
    fetchAuditLogs();
    fetchSummary();
  }, [filters]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      if (!token) {
        setError('Admin authentication required');
        return;
      }

      const response = await api.getAdminAuditLogs(token, filters);
      setAuditLogs(response.data.audit_logs);
      setPagination(response.data.pagination);
      setError(null);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) return;

      const response = await api.getAdminAuditLogsSummary(token);
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching audit summary:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value // Reset to page 1 when changing filters
    }));
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      per_page: 10,
      organization_id: '',
      category: '',
      action: '',
      actor_type: '',
      severity: '',
      status: '',
      start_date: '',
      end_date: ''
    });
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      success: 'bg-green-100 text-green-800 border-green-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
      warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      unauthorized: 'bg-orange-100 text-orange-800 border-orange-200'
    };

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${statusColors[status] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
        {status}
      </span>
    );
  };

  const getSeverityBadge = (severity) => {
    const severityColors = {
      low: 'bg-blue-100 text-blue-800 border-blue-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      critical: 'bg-red-100 text-red-800 border-red-200'
    };

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${severityColors[severity] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
        {severity}
      </span>
    );
  };

  const getActorTypeBadge = (actorType) => {
    const actorTypeColors = {
      user: 'bg-blue-100 text-blue-800 border-blue-200',
      admin: 'bg-purple-100 text-purple-800 border-purple-200',
      system: 'bg-gray-100 text-gray-800 border-gray-200'
    };

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${actorTypeColors[actorType] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
        {actorType}
      </span>
    );
  };

  const getCategoryIcon = (category) => {
    const icons = {
      agents: '🤖',
      clients: '📱',
      users: '👤',
      messages: '💬',
      documents: '📄',
      auth: '🔐',
      settings: '⚙️',
      admin: '🛡️',
      organizations: '🏢',
      system: '💻'
    };

    return icons[category] || '📋';
  };

  const exportLogs = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== 'page' && key !== 'per_page') {
          params.append(key, value);
        }
      });

      const response = await fetch(`/api/admin/audit-logs/export?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `admin-audit-logs-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting logs:', err);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading audit trails...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Shield className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">Platform Audit Trails</h1>
                  <p className="text-sm text-gray-600">Monitor all activities across organizations and administrative actions</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={exportLogs}
                  className="inline-flex items-center px-4 py-2 border border-green-300 shadow-sm text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 transition-colors"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </button>
                <button
                  onClick={fetchAuditLogs}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Activity className="h-8 w-8 text-blue-500" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total Actions</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {summary.summary.total_actions?.toLocaleString() || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Clock className="h-8 w-8 text-green-500" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Last 24h</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {summary.summary.actions_last_24h?.toLocaleString() || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Calendar className="h-8 w-8 text-purple-500" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Last 7 Days</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {summary.summary.actions_last_7d?.toLocaleString() || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertCircle className="h-8 w-8 text-red-500" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Failed Actions</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {summary.summary.failed_actions?.toLocaleString() || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Shield className="h-8 w-8 text-orange-500" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">High Severity</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {summary.summary.high_severity_actions?.toLocaleString() || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Building className="h-8 w-8 text-indigo-500" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Org.</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {summary.summary.active_organizations?.toLocaleString() || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <User className="h-8 w-8 text-teal-500" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Users</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {summary.summary.active_actors?.toLocaleString() || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-medium text-gray-900">Filter Audit Logs</h2>
                  <p className="text-sm text-gray-600 mt-1">Narrow down audit logs using the filters below</p>
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {showFilters ? 'Hide' : 'Show'} Filters
                </button>
              </div>
            </div>

            {showFilters && (
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-8 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Actor Type
                    </label>
                    <select
                      value={filters.actor_type}
                      onChange={(e) => handleFilterChange('actor_type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Types</option>
                      {actorTypes.map(type => (
                        <option key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      value={filters.category}
                      onChange={(e) => handleFilterChange('category', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Categories</option>
                      {categories.map(category => (
                        <option key={category} value={category}>
                          {category.charAt(0).toUpperCase() + category.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Action
                    </label>
                    <select
                      value={filters.action}
                      onChange={(e) => handleFilterChange('action', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Actions</option>
                      {actions.map(action => (
                        <option key={action} value={action}>
                          {action.charAt(0).toUpperCase() + action.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Severity
                    </label>
                    <select
                      value={filters.severity}
                      onChange={(e) => handleFilterChange('severity', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Severities</option>
                      {severities.map(severity => (
                        <option key={severity} value={severity}>
                          {severity.charAt(0).toUpperCase() + severity.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Statuses</option>
                      {statuses.map(status => (
                        <option key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={filters.start_date}
                      onChange={(e) => handleFilterChange('start_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={filters.end_date}
                      onChange={(e) => handleFilterChange('end_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Per Page
                    </label>
                    <select
                      value={filters.per_page}
                      onChange={(e) => handleFilterChange('per_page', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>

                  <div className="flex items-end">
                    <button
                      onClick={clearFilters}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Audit Logs Table */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-medium text-gray-900">Platform Activity Log</h2>
                  {pagination.total_count && (
                    <p className="text-sm text-gray-600 mt-1">
                      Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total_count)} of {pagination.total_count} activities
                    </p>
                  )}
                </div>
              </div>
            </div>

            {error && (
              <div className="px-6 py-4 bg-red-50 border-l-4 border-red-400">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resource
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Severity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {auditLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">{getCategoryIcon(log.category)}</span>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {log.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            {log.category} • {log.action}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{log.actor_name}</div>
                      <div className="text-sm text-gray-500">{log.actor_email}</div>
                      <div className="mt-1">{getActorTypeBadge(log.actor_type)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{log.organization_name || 'System'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{log.resource_name || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{log.resource_type}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(log.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getSeverityBadge(log.severity)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTimestamp(log.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => setSelectedLog(log)}
                        className="inline-flex items-center px-3 py-1.5 border border-blue-300 shadow-sm text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      disabled={!pagination.has_prev}
                      onClick={() => handleFilterChange('page', pagination.page - 1)}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>

                    <span className="text-sm text-gray-700">
                      Page {pagination.page} of {pagination.total_pages}
                    </span>

                    <button
                      disabled={!pagination.has_next}
                      onClick={() => handleFilterChange('page', pagination.page + 1)}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}

            {auditLogs.length === 0 && !loading && (
              <div className="text-center py-12">
                <Shield className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No audit logs found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  No audit logs match the current filters. Try adjusting your search criteria.
                </p>
              </div>
            )}
          </div>

          {/* Detail Modal */}
          {selectedLog && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
              <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-screen overflow-y-auto">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">Activity Details</h3>
                    <button
                      onClick={() => setSelectedLog(null)}
                      className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                      ×
                    </button>
                  </div>
                </div>

                <div className="px-6 py-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <strong className="text-gray-700">Activity:</strong>
                      <p className="text-gray-900">{selectedLog.title}</p>
                    </div>
                    <div>
                      <strong className="text-gray-700">Status:</strong>
                      <div className="mt-1">{getStatusBadge(selectedLog.status)}</div>
                    </div>
                    <div>
                      <strong className="text-gray-700">Actor:</strong>
                      <p className="text-gray-900">{selectedLog.actor_name} ({selectedLog.actor_email})</p>
                      <div className="mt-1">{getActorTypeBadge(selectedLog.actor_type)}</div>
                    </div>
                    <div>
                      <strong className="text-gray-700">Timestamp:</strong>
                      <p className="text-gray-900">{formatTimestamp(selectedLog.created_at)}</p>
                    </div>
                    <div>
                      <strong className="text-gray-700">Organization:</strong>
                      <p className="text-gray-900">{selectedLog.organization_name || 'System'}</p>
                    </div>
                    <div>
                      <strong className="text-gray-700">Severity:</strong>
                      <div className="mt-1">{getSeverityBadge(selectedLog.severity)}</div>
                    </div>
                    <div>
                      <strong className="text-gray-700">Category:</strong>
                      <p className="text-gray-900">{selectedLog.category}</p>
                    </div>
                    <div>
                      <strong className="text-gray-700">Action:</strong>
                      <p className="text-gray-900">{selectedLog.action}</p>
                    </div>
                    <div>
                      <strong className="text-gray-700">Resource:</strong>
                      <p className="text-gray-900">{selectedLog.resource_name || 'N/A'} ({selectedLog.resource_type})</p>
                    </div>
                    <div>
                      <strong className="text-gray-700">IP Address:</strong>
                      <p className="text-gray-900">{selectedLog.ip_address || 'N/A'}</p>
                    </div>
                  </div>

                  {selectedLog.description && (
                    <div>
                      <strong className="text-gray-700">Description:</strong>
                      <p className="text-gray-900 mt-1">{selectedLog.description}</p>
                    </div>
                  )}

                  {selectedLog.error_message && (
                    <div>
                      <strong className="text-gray-700">Error:</strong>
                      <p className="text-red-600 mt-1">{selectedLog.error_message}</p>
                    </div>
                  )}

                  {selectedLog.endpoint && (
                    <div>
                      <strong className="text-gray-700">Endpoint:</strong>
                      <p className="text-gray-900 font-mono">{selectedLog.http_method} {selectedLog.endpoint}</p>
                    </div>
                  )}

                  {selectedLog.details && (
                    <div>
                      <strong className="text-gray-700">Additional Details:</strong>
                      <pre className="text-sm text-gray-900 bg-gray-100 p-3 rounded mt-1 overflow-x-auto">
                        {JSON.stringify(selectedLog.details, null, 2)}
                      </pre>
                    </div>
                  )}

                  {selectedLog.changes && (
                    <div>
                      <strong className="text-gray-700">Changes:</strong>
                      <pre className="text-sm text-gray-900 bg-gray-100 p-3 rounded mt-1 overflow-x-auto">
                        {JSON.stringify(selectedLog.changes, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAuditTrails;